"use client";

import { Modal as BricksModal } from "@red-digital/bricks";
import React, { ReactNode } from "react";
import EventClient from "@2u-landing-pages/utils/event-client";
// @ts-ignore
import styles from "./Modal.module.scss";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  showCloseButton?: boolean;
  closeButtonPosition?: "right" | "left";
  closeButtonLocation?: "inside" | "outside";
  customCloseButton?: React.FC;
  onAfterClose?: () => void;
  debugOverride?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  showCloseButton = false,
  closeButtonPosition = "right",
  closeButtonLocation = "outside",
  customCloseButton,
  onAfterClose,
  debugOverride = "False",
}) => {
  const modalRef = React.useRef<HTMLDivElement>(null);

  const shouldShowModal = React.useMemo(() => {
    if (debugOverride === "True") return true;
    return isOpen;
  }, [isOpen, debugOverride]);

  const handleClose = React.useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
    // Don't allow closing when debugOverride is active in development
    if (debugOverride === "True") return;

    EventClient.elementClicked({
      webElement: {
        elementType: "Modal Close",
        htmlId: event?.currentTarget ? "button" : "background",
        location: "Modal",
        position: event?.currentTarget ? "button" : "background",
        text: "Close",
      },
    });

    onClose();

    // Ensure the body overflow is reset
    requestAnimationFrame(() => {
      document.body.style.overflow = "auto";
    });

    if (onAfterClose) {
      queueMicrotask(() => onAfterClose());
    }
  }, [onClose, onAfterClose, debugOverride]);

  // Remove focus from close button when modal opens
  React.useEffect(() => {
    if (shouldShowModal && modalRef.current) {
      const animationFrameId = requestAnimationFrame(() => {
        const closeButton = modalRef.current?.querySelector("button.b-modal__close");
        if (closeButton && document.activeElement === closeButton) {
          (closeButton as HTMLElement).blur();
        }
      });

      return () => cancelAnimationFrame(animationFrameId);
    }
  }, [shouldShowModal]);

  return (
    <div className={styles.modalWrapper} ref={modalRef}>
      <BricksModal
        active={shouldShowModal}
        className={styles.modal}
        close={{
          callback: handleClose,
          buttonEnabled: showCloseButton,
          position: closeButtonPosition,
          location: closeButtonLocation,
          component: customCloseButton,
        }}
      >
        {children}
      </BricksModal>
    </div>
  );
};
